import { Link as RouterLink } from "react-router";
import { hideVisually, size } from "polished";
import React, { useEffect } from "react";
import {
  WithTranslation,
  useTranslation,
  withTranslation,
} from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getEntry,
  getPrivateClassicLeaguesForEntry,
  getPrivateH2HLeaguesForEntry,
  getPublicClassicLeaguesForEntry,
  getPublicH2HLeaguesForEntry,
  getSystemClassicLeaguesForEntry,
} from "core-integration/src/store/entries/reducers";
import { fetchEntrySummary } from "core-integration/src/store/entries/thunks";
import { ILeagueEntry } from "core-integration/src/store/entries/types";
import { leaveLeague } from "core-integration/src/store/leagues/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { ILoggedInPlayer } from "core-integration/src/store/player/types";
import BaseCog from "../../img/icons/cog.svg?react";
import Button from "../Button";
import Dialog, { DialogButtonItem } from "../Dialog";
import DialogHeading from "../DialogHeading";
import DialogManager from "../DialogManager";
import { Main, Secondary, Wrapper } from "../Layout";
import Link from "../Link";
import LinkButton from "../LinkButton";
import Panel from "../Panel";
import SubHeading from "../SubHeading";
import Table from "../Table";
import { ControlArrowRight } from "../icons/Arrows";
import LeaguesNav from "./LeaguesNav";
import Movement from "./Movement";
import { getLeagueUrl } from "./utils";

const ActionBar = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    display: flex;
  }
`;

const ActionBarItem = styled.li`
  padding: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    flex: 1;
  }
`;

const ActionList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 50%;
    margin: 0 auto;
  }
`;

const MyLeaguesTable = styled(Table)`
  table-layout: fixed;

  th {
    padding-left: ${({ theme }) => theme.space[1]};
    padding-right: ${({ theme }) => theme.space[1]};

    @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
      padding-left: ${({ theme }) => theme.space[2]};
      padding-right: ${({ theme }) => theme.space[2]};
    }
  }

  td {
    padding: ${({ theme }) => theme.space[3]} ${({ theme }) => theme.space[1]};

    @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
      padding-left: ${({ theme }) => theme.space[2]};
      padding-right: ${({ theme }) => theme.space[2]};
    }
  }
`;

const LeagueCol = styled.th`
  width: 40%;
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    width: 36%;
  }
`;

const MovementCol = styled.th`
  width: 8%;
`;

const RankCol = styled.th`
  width: 24%;
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    width: 18%;
  }
`;

const OptionsCol = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    width: 20%;
  }
`;

const MenuCopy = styled.span`
  margin-left: ${({ theme }) => theme.space[1]};

  @media (max-width: ${({ theme }) => theme.breakpoints[2]}) {
    ${hideVisually}
  }
`;

const Cog = styled(BaseCog)`
  ${size(14)};
`;

type Props = WithTranslation;

const MyLeagues = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const player = useSelector(getPlayerData) as ILoggedInPlayer; // enforced by EntryRoute
  const entry = useSelector((state: RootState) =>
    getEntry(state, player.entry),
  );
  const privateClassicLeagues = useSelector((state: RootState) =>
    getPrivateClassicLeaguesForEntry(state, player.entry),
  );
  const privateH2HLeagues = useSelector((state: RootState) =>
    getPrivateH2HLeaguesForEntry(state, player.entry),
  );
  const publicClassicLeagues = useSelector((state: RootState) =>
    getPublicClassicLeaguesForEntry(state, player.entry),
  );
  const publicH2HLeagues = useSelector((state: RootState) =>
    getPublicH2HLeaguesForEntry(state, player.entry),
  );
  const systemClassicLeagues = useSelector((state: RootState) =>
    getSystemClassicLeaguesForEntry(state, player.entry),
  );
  const leave = (leagueId: number) => {
    if (
      window.confirm(
        t<string>(
          "myLeagues.confirmLeave",
          "Are you sure you want to leave this league?",
        ),
      )
    ) {
      dispatch(leaveLeague(leagueId));
    }
  };

  useEffect(() => {
    dispatch(fetchEntrySummary(player.entry));
  }, [dispatch, player.entry]);

  if (!entry) {
    return null;
  }
  return (
    <Wrapper>
      <Main>
        <Box pb={4}>
          <Panel hasKeyline={false}>
            <Panel.Header title={t("myLeagues.title", "My Leagues")} />
            <Panel.Body>
              <Box mx={2} mt={2}>
                <SubHeading>{entry.name}</SubHeading>
                <LeaguesNav />
              </Box>
              <ActionBar>
                <ActionBarItem>
                  <Button
                    width={1}
                    as={RouterLink}
                    to="create-join"
                    endIcon={<ControlArrowRight />}
                  >
                    {t(
                      "myLeagues.createJoinButton",
                      "Create and join new leagues & cups",
                    )}
                  </Button>
                </ActionBarItem>
                <ActionBarItem>
                  <Button
                    width={1}
                    variant="secondary"
                    as={RouterLink}
                    to="renew"
                    endIcon={<ControlArrowRight />}
                  >
                    {t("myLeagues.renewButton", "Renew your leagues")}
                  </Button>
                </ActionBarItem>
              </ActionBar>
            </Panel.Body>
          </Panel>
        </Box>

        <Box pb={4}>
          <LeagueTypeTable
            title={t(
              "myLeagues.tableTitles.privateClassic",
              "Private classic leagues",
            )}
            leagues={privateClassicLeagues}
            leave={leave}
          />
        </Box>
        <Box pb={4}>
          <LeagueTypeTable
            title={t(
              "myLeagues.tableTitles.privateH2H",
              "Private head-to-head leagues",
            )}
            leagues={privateH2HLeagues}
            leave={leave}
          />
        </Box>
        <Box pb={4}>
          <LeagueTypeTable
            title={t(
              "myLeagues.tableTitles.publicClassic",
              "Public classic leagues",
            )}
            leagues={publicClassicLeagues}
            leave={leave}
          />
        </Box>
        <Box pb={4}>
          <LeagueTypeTable
            title={t(
              "myLeagues.tableTitles.publicH2H",
              "Public head-to-head leagues",
            )}
            leagues={publicH2HLeagues}
            leave={leave}
          />
        </Box>
        <Box pb={4}>
          <LeagueTypeTable
            title={t("myLeagues.tableTitles.global", "Global leagues")}
            leagues={systemClassicLeagues}
            leave={leave}
          />
        </Box>
      </Main>
      <Secondary />
    </Wrapper>
  );
};

export default withTranslation()(MyLeagues);

interface ILeagueTypeTableProps {
  leagues: ILeagueEntry[];
  leave: (leagueId: number) => void;
  title: string;
}

const LeagueTypeTable: React.FC<ILeagueTypeTableProps> = ({
  leagues,
  leave,
  title,
}) => {
  const { t } = useTranslation();
  return (
    <Panel hasKeyline={false}>
      <Panel.Header title={title} />
      <Panel.Body>
        {leagues.length === 0 ? (
          <Box m={2}>
            <p>
              {t("myLeagues.noneJoined", "No ")}
              {title.toLowerCase()}
              {t("myLeagues.noneJoined2", " joined yet.")}{" "}
              <Link to="create-join">
                {t("myLeagues.noneJoined3", "Create and join new leagues")}
              </Link>
              .
            </p>
          </Box>
        ) : (
          <MyLeaguesTable>
            <thead>
              <tr>
                <LeagueCol>{t("myLeagues.th.league", "League")}</LeagueCol>
                <MovementCol>&nbsp;</MovementCol>
                <RankCol>
                  {t("myLeagues.th.currentRank", "Current Rank")}
                </RankCol>
                <RankCol>{t("myLeagues.th.lastRank", "Last Rank")}</RankCol>
                <OptionsCol>&nbsp;</OptionsCol>
              </tr>
            </thead>
            <tbody>
              {leagues.map((l) => (
                <tr key={l.id}>
                  <td>
                    <Link to={getLeagueUrl(l.id, l.scoring)}>
                      <div>{l.name}</div>
                    </Link>
                  </td>
                  <td>
                    <Movement
                      lastRank={l.entry_last_rank}
                      rank={l.entry_rank}
                    />
                  </td>
                  <td>{l.entry_rank ? l.entry_rank.toLocaleString() : "-"}</td>
                  <td>
                    {l.entry_last_rank
                      ? l.entry_last_rank.toLocaleString()
                      : "-"}
                  </td>
                  <td>
                    <DialogManager
                      render={(showDialog, handleShow, handleHide) => (
                        <>
                          <LinkButton onClick={handleShow}>
                            <Cog />
                            <MenuCopy>
                              {t("myLeagues.optionsLink", "Options")}
                            </MenuCopy>
                          </LinkButton>
                          {showDialog && (
                            <LeagueMenuDialog
                              leagueEntry={l}
                              leave={leave}
                              handleHide={handleHide}
                            />
                          )}
                        </>
                      )}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </MyLeaguesTable>
        )}
      </Panel.Body>
    </Panel>
  );
};

interface ILeagueMenuDialogProps {
  handleHide: () => void;
  leagueEntry: ILeagueEntry;
  leave: (leagueId: number) => void;
}

const LeagueMenuDialog: React.FC<ILeagueMenuDialogProps> = ({
  handleHide,
  leagueEntry,
  leave,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog closeDialog={handleHide}>
      <Dialog.Header closeDialog={handleHide}>
        <Box py={2} px={3}>
          <DialogHeading>{leagueEntry.name}</DialogHeading>
        </Box>
      </Dialog.Header>
      <Dialog.Body isPadded={true}>
        <ActionList>
          <DialogButtonItem>
            <Button
              width={1}
              as={RouterLink}
              endIcon={<ControlArrowRight />}
              to={`/leagues/${leagueEntry.id}/standings/${leagueEntry.scoring}`}
            >
              {t("myLeagues.menuDialog.standings", "Standings")}
            </Button>
          </DialogButtonItem>
          {leagueEntry.entry_can_leave && (
            <DialogButtonItem>
              <Button
                width={1}
                endIcon={<ControlArrowRight />}
                onClick={() => {
                  leave(leagueEntry.id);
                  handleHide();
                }}
              >
                {t("myLeagues.menuDialog.leave", "Leave league")}
              </Button>
            </DialogButtonItem>
          )}
          {leagueEntry.entry_can_admin && (
            <DialogButtonItem>
              <Button
                width={1}
                as={RouterLink}
                endIcon={<ControlArrowRight />}
                to={`/leagues/${leagueEntry.id}/admin/${leagueEntry.scoring}`}
              >
                {t("myLeagues.menuDialog.admin", "Administer")}
              </Button>
            </DialogButtonItem>
          )}
          {leagueEntry.entry_can_invite && (
            <DialogButtonItem>
              <Button
                width={1}
                as={RouterLink}
                endIcon={<ControlArrowRight />}
                to={`/leagues/${leagueEntry.id}/invite`}
              >
                {t("myLeagues.menuDialog.invite", "Invite friends")}
              </Button>
            </DialogButtonItem>
          )}
        </ActionList>
      </Dialog.Body>
    </Dialog>
  );
};
